import { unitTitle } from '../copy';
import { type ICancellationAlert } from './cancellation-alerts';

/**
 * Opt-in sound + browser notification for cancellation alerts.
 *
 * Never prompted automatically: the permission request runs only from the "Get desktop
 * alerts" button (a user gesture, which is also what unlocks WebAudio). A denial is
 * remembered so the offer is not shown again; the in-page stack keeps working either way.
 */

export type DesktopAlertState = 'unsupported' | 'off' | 'on' | 'denied';

const PREF_KEY = 'pu-cancel-desktop:v1';

type AudioCtor = typeof AudioContext;
let audioCtx: AudioContext | null = null;

const readPref = (): string | null => {
	try {
		return window.localStorage.getItem(PREF_KEY);
	} catch {
		return null;
	}
};

const writePref = (value: 'on' | 'denied' | 'off') => {
	try {
		window.localStorage.setItem(PREF_KEY, value);
	} catch {
		// The choice just won't survive a reload.
	}
};

export const notificationSupported = (): boolean => typeof window !== 'undefined' && 'Notification' in window;

export const desktopAlertState = (): DesktopAlertState => {
	if (!notificationSupported()) return 'unsupported';
	if (Notification.permission === 'denied') return 'denied';
	const pref = readPref();
	if (pref === 'denied') return 'denied';
	if (pref === 'on' && Notification.permission === 'granted') return 'on';
	return 'off';
};

const audioCtor = (): AudioCtor | null => {
	const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
	return w.AudioContext ?? w.webkitAudioContext ?? null;
};

/** Must run inside a click handler. */
export const enableDesktopAlerts = async (): Promise<DesktopAlertState> => {
	const Ctor = audioCtor();
	if (Ctor && !audioCtx) {
		try {
			audioCtx = new Ctor();
		} catch {
			audioCtx = null;
		}
	}
	if (!notificationSupported()) return 'unsupported';
	let permission: NotificationPermission = Notification.permission;
	if (permission === 'default') {
		try {
			permission = await Notification.requestPermission();
		} catch {
			permission = 'denied';
		}
	}
	if (permission === 'granted') {
		writePref('on');
		return 'on';
	}
	writePref('denied');
	return 'denied';
};

export const disableDesktopAlerts = () => writePref('off');

/** Two short descending tones — distinct from the OS notification sound, never looped. */
const chime = () => {
	if (!audioCtx) {
		const Ctor = audioCtor();
		if (!Ctor) return;
		try {
			audioCtx = new Ctor();
		} catch {
			return;
		}
	}
	const ctx = audioCtx;
	if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
	const start = ctx.currentTime;
	[880, 660].forEach((freq, i) => {
		const osc = ctx.createOscillator();
		const gain = ctx.createGain();
		osc.type = 'sine';
		osc.frequency.value = freq;
		const t = start + i * 0.18;
		gain.gain.setValueAtTime(0.0001, t);
		gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
		gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
		osc.connect(gain).connect(ctx.destination);
		osc.start(t);
		osc.stop(t + 0.17);
	});
};

/** Called only for the tab that won `claimChime`. */
export const ringFor = (alert: ICancellationAlert, onOpen?: () => void) => {
	if (desktopAlertState() !== 'on') return;
	chime();
	try {
		const n = new Notification(
			alert.active ? `Polling unit cancelled: ${unitTitle(alert.row)}` : `Cancellation withdrawn: ${unitTitle(alert.row)}`,
			{ body: alert.row.pollingUnitCode, tag: alert.signature }
		);
		n.onclick = () => {
			window.focus();
			onOpen?.();
			n.close();
		};
	} catch {
		// Some platforms only allow notifications from a service worker — the sound already played.
	}
};
